import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import {
  StoryDifficultyLevel,
  LEVEL_OPTIONS,
  updatePreferredStoryLevel,
  getPreferredStoryLevel,
  clearPreferredLevelSyncState,
} from '../../redux/actions/storyLevelAction'
import NotificationBox from '../../containers/form/NotificationBox'
import { resetPlacementTest } from '../../redux/actions/myAccountAction'
import { useNavigate } from 'react-router-dom'
import { CHINESE_LANGUAGE_ID, membershipNameType } from '../../utils/constant'
import '../../components/landing-page/stories.css'
import { ONBOARDING_PERSIST_STORAGE_KEY } from '../../redux/store'
import { SettingsSection } from '../setting/SettingsSection'

interface IStoryDifficultyProps {
  credentials?: any
  membership?: any
}

const StoryDifficulty: React.FC<IStoryDifficultyProps> = (props: any) => {
  const { credentials, membership } = props
  const { t } = useTranslation('account')
  const navigate = useNavigate()
  const isWeekly = membership?.name === membershipNameType.Weekly
  const [selectedLevel, setSelectedLevel] = useState<StoryDifficultyLevel | null>(null)
  const [notify, setNotify] = useState({ label: '', isError: false })
  const [isResetting, setIsResetting] = useState(false)

  const clearNotify = () => {
    setNotify({ label: '', isError: false })
  }

  useEffect(() => {
    if (CHINESE_LANGUAGE_ID) {
      getPreferredStoryLevel(CHINESE_LANGUAGE_ID).then((level) => {
        if (level) {
          setSelectedLevel(level as StoryDifficultyLevel)
        }
      })
    }
  }, [])

  const handleLevelSelect = async (level: StoryDifficultyLevel) => {
    setSelectedLevel(level)
    if (CHINESE_LANGUAGE_ID) {
      try {
        await updatePreferredStoryLevel(level, CHINESE_LANGUAGE_ID)
        // Weekly subscribers read a frozen weekly pin (mirrors the /stories level changer):
        // a mid-week level change applies at the next Sunday drop, not now. Clear the
        // preferred-level sync marker that updatePreferredStoryLevel writes so the /stories
        // page never enters its refresh/poll state — the current week's stories stay static.
        if (isWeekly) {
          clearPreferredLevelSyncState()
          setNotify({
            label: t('level.savedWeekly'),
            isError: false,
          })
        } else {
          setNotify({ label: t('level.saved'), isError: false })
        }
        setTimeout(() => clearNotify(), 3000)
      } catch (error) {
        setNotify({ label: t('level.saveError'), isError: true })
      }
    }
  }

  const handleResetPlacement = async () => {
    if (isResetting) return
    clearNotify()
    setIsResetting(true)
    const response = await resetPlacementTest({ language_id: credentials?.language_id })
    setIsResetting(false)

    if (response?.message) {
      setNotify({ label: t('level.placement.reset'), isError: false })
      localStorage.removeItem(ONBOARDING_PERSIST_STORAGE_KEY)
      navigate('/learn-chinese/existing')
    } else {
      const message = response?.message || t('level.placement.resetError')
      setNotify({ label: message, isError: true })
    }
  }

  return (
    <>
      <h4 className={'m-h4 m-gray-1'}>{t('level.title')}</h4>
      <NotificationBox clearNotify={clearNotify} {...notify} />

      <div className='settings-section'>
        <div className='browse-level-tabs'>
          {LEVEL_OPTIONS.map((option) => (
            <button
              key={option.value}
              className={`browse-level-tab ${selectedLevel === option.value ? 'browse-level-tab--active' : ''}`}
              aria-pressed={selectedLevel === option.value}
              onClick={() => handleLevelSelect(option.value)}
            >
              <span className='browse-level-tab-label'>{option.label}</span>
              <span className='browse-level-tab-description'>{option.description}</span>
            </button>
          ))}
        </div>
      </div>

      <SettingsSection
        title={t('level.placement.title')}
        note={t('level.placement.note')}
      >
        <button className='settings-text-button' onClick={handleResetPlacement} disabled={isResetting}>
          {isResetting ? t('level.placement.resetting') : t('level.placement.retake')}
        </button>
      </SettingsSection>
    </>
  )
}

const mapStateToProps = (state: any) => ({
  credentials: state.user.credentials,
  membership: state.user.membership,
})

export default connect(mapStateToProps)(StoryDifficulty)
